import { useState } from "react"

import { InboxOutlined } from "@ant-design/icons"
import { Image as AntdImage, App, Typography, Upload } from "antd"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../../Components/Button"
import { modalConfig } from "../../../../../../../theme"
import {
  DEFAULT_UPLOAD_ACCEPT,
  DEFAULT_UPLOAD_HINT,
  isAcceptedUpload,
  UPLOAD_FAILED_MESSAGE,
  UPLOAD_MAX_BYTES
} from "./uploadRules"

import uploadStyles from "../../../../../uploadSlot.module.less"

const { Text } = Typography

const mainLayout = () => document.getElementById("main-layout")

const UploadControl = ({ question, value, onChange, onTouch, onUpload, onRemove }) => {
  const { t } = useTranslation()
  // Contextual confirm modal (the same instance the Integrate / Footer steps use for
  // their destructive confirms).
  const { modal } = App.useApp()

  const [rejected, setRejected] = useState(false)

  const preview = value?.url
  const fileName = value?.file_name || value?.s3_key?.split("/").pop()
  const accept = question.accept || DEFAULT_UPLOAD_ACCEPT
  const hint = question.acceptHint || DEFAULT_UPLOAD_HINT
  const isImagePreview = preview && /^data:image|\.(png|jpe?g|svg|gif|webp)(\?|$)/i.test(preview)
  const displaySrc = preview

  const beforeUpload = (file) => {
    onTouch()
    const accepted = isAcceptedUpload(file, accept, question.maxBytes || UPLOAD_MAX_BYTES)
    setRejected(!accepted)
    if (accepted) onUpload?.(question.key, file)
    return false // never POST from here; the caller owns the upload
  }
  const rejectedNote = rejected ? <Text type="danger">{t(UPLOAD_FAILED_MESSAGE)}</Text> : null

  // Removing an upload is destructive, so gate it behind the same red confirm modal the
  // rest of the Configure flow uses for disconnects.
  const confirmRemove = () =>
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: true,
      title: t("Remove _name_?", { name: t(question.label) }),
      content: t(
        "This removes the uploaded file from your evidence locker. You can upload a new one any time."
      ),
      okText: t("Remove"),
      okButtonProps: { ...modalConfig("exclamation").okButtonProps, danger: true },
      cancelText: t("Cancel"),
      getContainer: mainLayout,
      onOk: () => {
        onTouch()
        setRejected(false)
        if (onRemove) onRemove(question.key)
        else onChange(null)
      }
    })

  // Once an image is uploaded, swap the drop target for a preview card: a
  // click-to-expand thumbnail with Replace / Remove. Non-image files keep the dragger
  // plus a filename link.
  if (isImagePreview) {
    return (
      <div className={`${uploadStyles.slot} ${uploadStyles.slotDone}`}>
        <AntdImage
          src={displaySrc}
          alt={t(question.label)}
          rootClassName={uploadStyles.thumb}
          preview={{ src: displaySrc, mask: t("Click to expand"), getContainer: mainLayout }}
        />
        <div className={uploadStyles.actions}>
          <Upload showUploadList={false} accept={accept} beforeUpload={beforeUpload}>
            <Button type="default" className="compact-btn" text={t("Replace")} />
          </Upload>
          <Button
            type="text"
            danger
            className="compact-btn"
            onClick={confirmRemove}
            text={t("Remove")}
          />
        </div>
        {rejectedNote}
      </div>
    )
  }

  return (
    <div className={uploadStyles.slot}>
      <Upload.Dragger
        multiple={false}
        showUploadList={false}
        accept={accept}
        beforeUpload={beforeUpload}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t("Click or drag a file here to upload")}</p>
        <p className="ant-upload-hint">{t(hint)}</p>
      </Upload.Dragger>
      {/* The wrapper is layout, not styling: .slot is a stretch-aligned column, so
          without it the inline-flex chip below would stretch the full column width. */}
      {preview ? (
        <div>
          <a href={preview} target="_blank" rel="noreferrer" className={uploadStyles.fileLink}>
            {fileName || t("View uploaded file")}
          </a>
        </div>
      ) : null}
      {rejectedNote}
    </div>
  )
}

export default UploadControl
